import { useAppSelector } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import Divider from 'flow/components/ui-components/Divider';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import useAppContext from 'flow/hooks/useAppContext';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import useVoicemail from 'flow/hooks/useVoicemail';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';

import {
    selectHasCapability,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { Capabilities } from 'flow/utils/capability-utils';
import Page from './Page';
import SettingSection from './SettingSection';
import VoicemailNumberOptions from './Voicemail/VoicemailNumberOptions';
import PlaybackMessage from './Voicemail/PlaybackMessage';
import VoicemailNotifications from './Voicemail/VoicemailNotifications';

const Voicemail = () => {
    const { i18next } = useAppContext();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const history = useHistory();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { isSinglePaneMode } = useSinglePaneMode();
    const { goToSettings } = useSettingsNavigation();
    const { voicemailSettings } = useVoicemail(extensionKey);
    const hasCapabilityVoicemail = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.VOICEMAIL),
    );

    const isOwnExtensionKey = extensionKey === ownExtensionKey;

    const EmptyView = (
        <Page
            header="settings.voicemail"
            onClickBack={
                isSinglePaneMode
                    ? () =>
                          history.push(
                              generatePath(routes['settings'], {
                                  extensionKey,
                              }),
                          )
                    : undefined
            }
        >
            <div className="text-center text-gray-400">
                {i18next.t('settings.voicemail_no_access')}
            </div>
        </Page>
    );

    if (!hasCapabilityVoicemail) {
        return EmptyView;
    } else if (!voicemailSettings) {
        return <Loading />;
    }

    return (
        <Page
            header="settings.voicemail"
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
        >
            {isOwnExtensionKey && (
                <>
                    <SettingSection>
                        {i18next.t('settings.voicemail_playback')}
                    </SettingSection>

                    <PlaybackMessage extensionKey={extensionKey} type="busy" />
                    <PlaybackMessage
                        extensionKey={extensionKey}
                        type="unavail"
                    />

                    <Divider />

                    <RouteLink to={routes['settings.voicemail.sound']}>
                        {i18next.t('settings.voicemail.sound')}
                    </RouteLink>

                    <Divider />
                </>
            )}

            <SettingSection>
                {i18next.t('settings.voicemail_settings')}
            </SettingSection>

            <RouteLink
                to={routes['settings.voicemail.login']}
                description={
                    voicemailSettings?.get('autoLogin')
                        ? i18next.t('option.no')
                        : i18next.t('option.yes')
                }
            >
                {i18next.t('settings.voicemail_login')}
            </RouteLink>

            <VoicemailNotifications extensionKey={extensionKey} />

            <RouteLink
                to={routes['settings.voicemail.alternatives']}
                className="py-4"
            >
                {i18next.t('settings.voicemail_alternatives')}
            </RouteLink>

            {isOwnExtensionKey && (
                <>
                    <Divider />

                    <VoicemailNumberOptions extensionKey={extensionKey} />
                </>
            )}
        </Page>
    );
};

export default Voicemail;
